import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import {
  massagerLoginFormSchema,
  type MassagerLoginFormData,
} from '@/schemas/massagerLoginSchema';
import {
  AppAdminFormLabel,
  AppButton,
  AppForm,
  AppTextField,
  CountdownErrorMessage,
} from '@/components/atoms';
import useAppToast from '@/hooks/useAppToast';
import type { MassagerLoginCredentials } from '@/types/massagerAuth';

const MAX_INPUT_LENGTH = 255;
const TOAST_ERROR_MESSAGE = 'エラーが発生しました。';
const VALIDATION_TOAST_KEY = 'massager-login-validation-error';

/** Enforce maxLength on Ant Design Input (which only shows a counter by default). */
const enforceMaxLength = (
  e: React.ChangeEvent<HTMLInputElement>,
  fieldOnChange: (...args: unknown[]) => void,
) => {
  const value = e.target.value.length > MAX_INPUT_LENGTH
    ? e.target.value.slice(0, MAX_INPUT_LENGTH)
    : e.target.value;
  fieldOnChange(value);
};

/**
 * Massager (checkin site) login page.
 *
 * Form fields mirror RA2 Massager\LoginController: clinic_number + login_id + password.
 * Mobile-first layout — the checkin site is used primarily on phones.
 */
const MassagerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { site } = useParams<{ site: string }>();
  const { colors } = useTheme();
  const { login, isLoading } = useMassagerAuthContext();
  const toast = useAppToast();

  const { control, handleSubmit, setValue, setError, clearErrors } =
    useTrimmedForm<MassagerLoginFormData>({
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      resolver: zodResolver(massagerLoginFormSchema),
      defaultValues: {
        clinic_number: '',
        login_id: '',
        password: '',
      },
    });

  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
  } = useTenantSiteCheck(site);

  const validSite =
    !!site &&
    !isCheckingSite &&
    !isSiteCheckError &&
    !!siteCheck?.success &&
    !!siteCheck.data?.exists;

  const onValid = async (data: MassagerLoginFormData) => {
    if (!validSite || !site) return;

    clearErrors();

    const credentials: MassagerLoginCredentials = {
      clinic_number: data.clinic_number.trim(),
      login_id: data.login_id.trim(),
      password: data.password.trim(),
      site_type: site,
    };

    const result = await login(credentials);
    if (result.success) {
      navigate(`/${site}/massager/home`, { replace: true });
      return;
    }

    toast.error(TOAST_ERROR_MESSAGE);
    setValue('password', '', { shouldValidate: false, shouldDirty: false });
    setError('password', { type: 'server', message: result.message });
  };

  const onInvalid = () => {
    toast.error(TOAST_ERROR_MESSAGE, { key: VALIDATION_TOAST_KEY });
    setValue('password', '', { shouldValidate: false, shouldDirty: false });
  };

  if (site && !isCheckingSite && (!validSite || isSiteCheckError)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center overflow-x-hidden bg-[#fa7c64] px-4 py-8">
        <div className="w-full max-w-[420px] rounded-lg bg-white px-6 py-6 text-center shadow-lg">
          <h1 className="mb-3 text-lg font-bold text-red-600">無効なサイト</h1>
          <p className="mb-4 text-sm text-gray-600">指定されたサイトは存在しません。</p>
          <AppButton
            type="primary"
            onClick={() => navigate('/medix/massager/login')}
            style={{ backgroundColor: colors.primary }}
            className="w-full sm:w-auto"
          >
            ログイン画面へ
          </AppButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#fa7c64]">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <div className="my-4 text-center sm:my-8">
            <img
              src="/images/login/logo.png"
              alt="RA2N Massager Logo"
              className="mx-auto w-3/4 sm:w-full"
            />
          </div>

          <div className="rounded-lg bg-white px-5 py-6 shadow-lg sm:px-8">

            <form onSubmit={handleSubmit(onValid, onInvalid)} className="space-y-3">
              <Controller
                name="clinic_number"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppAdminFormLabel label="治療院ID" className="mb-1" />
                    <AppTextField
                      {...field}
                      onChange={(e) => enforceMaxLength(e, field.onChange)}
                      id="clinic_number"
                      className="w-full"
                      autoComplete="username"
                      autoFocus
                      maxLength={MAX_INPUT_LENGTH}
                      data-testid="massager-clinic-number-input"
                      size="large"
                    />
                  </AppForm.Item>
                )}
              />

              <Controller
                name="login_id"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppAdminFormLabel label="施術者ログインID" className="mb-1" />
                    <AppTextField
                      {...field}
                      onChange={(e) => enforceMaxLength(e, field.onChange)}
                      id="login_id"
                      className="w-full"
                      autoComplete="off"
                      maxLength={MAX_INPUT_LENGTH}
                      data-testid="massager-login-id-input"
                      size="large"
                    />
                  </AppForm.Item>
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => {
                  const isFieldLevelError =
                    !!fieldState.error && fieldState.error.type !== 'server';
                  return (
                    <AppForm.Item
                      validateStatus={isFieldLevelError ? 'error' : undefined}
                      help={
                        fieldState.error?.message
                          ? (
                            <span className={isFieldLevelError ? undefined : 'text-red-500'}>
                              <CountdownErrorMessage message={fieldState.error.message} />
                            </span>
                          )
                          : undefined
                      }
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="パスワード" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="password"
                        type="password"
                        className="w-full"
                        autoComplete="new-password"
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-password-input"
                        size="large"
                      />
                    </AppForm.Item>
                  );
                }}
              />

              <div className="pt-2">
                <AppButton
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={isLoading}
                  disabled={isLoading}
                  className="w-full transition-opacity duration-200 hover:opacity-90"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="massager-login-submit"
                >
                  {isLoading ? 'ログイン中...' : 'ログイン'}
                </AppButton>
              </div>
            </form>

            <div className="mt-4 text-right">
              <Link
                to={`/${site || 'medix'}/massager/password/reset`}
                className="text-sm hover:opacity-80"
                style={{ color: colors.primary }}
                data-testid="massager-forgot-password-link"
              >
                パスワードを忘れた方はこちら
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MassagerLoginPage;
